import React, { useState, useEffect } from 'react'

import { Tag, List, Empty, Collapse, Spin, message } from 'antd'
import { useTranslation } from 'react-i18next'

import { formatDateTime } from '../../utils/dateTime'

function getHttpStatusColor(statusCode) {
  if (statusCode >= 200 && statusCode < 300) return 'success'
  if (statusCode >= 400 && statusCode < 500) return 'warning'
  if (statusCode >= 500) return 'error'
  return 'default'
}

// 注意：platform/partner log 的 response_status 是 enum 字串（success/failed/unknown），
// webhook 的 response_status 是 HTTP 整數；這裡只服務 platform/partner 分支。
function getStatusTagColor(responseStatus, statusCode) {
  if (responseStatus === 'success') return 'success'
  if (responseStatus === 'failed') return 'error'
  return getHttpStatusColor(statusCode)
}

/**
 * ApiLogsPanel - 共用串接紀錄面板
 *
 * 整合三種 log 來源（平台串接、Webhook、Partners API），
 * 並行載入後合併以 created_at 降序排列。
 *
 * @param {string} loggableType - 關聯的 model 名稱 (e.g. 'Order', 'Product', 'Inbound')
 * @param {string} loggableId - 關聯的 record ID
 * @param {Object} [thirdPartyApiLogService] - 平台串接 service (需有 getList)
 * @param {Object} [webhookDeliveryService] - Webhook 發送 service (需有 getList)
 * @param {Object} [partnerApiLogService] - Partners API service (需有 getList)
 */
export default function ApiLogsPanel({
  loggableType,
  loggableId,
  thirdPartyApiLogService,
  webhookDeliveryService,
  partnerApiLogService,
}) {
  const { t } = useTranslation()
  const [logs, setLogs] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadLogs()
  }, [loggableType, loggableId])

  const loadLogs = async () => {
    if (loading) return
    setLoading(true)
    try {
      const params = { loggable_type: loggableType, loggable_id: loggableId }
      const fetches = []

      if (thirdPartyApiLogService) {
        fetches.push(
          thirdPartyApiLogService.getList(params)
            .then(res => (res.data || []).map(log => ({ ...log, _log_type: 'platform' })))
            .catch(() => [])
        )
      }

      if (webhookDeliveryService) {
        fetches.push(
          webhookDeliveryService.getList(params)
            .then(res => (res.data || []).map(log => ({ ...log, _log_type: 'webhook' })))
            .catch(() => [])
        )
      }

      if (partnerApiLogService) {
        fetches.push(
          partnerApiLogService.getList(params)
            .then(res => (res.data || []).map(log => ({ ...log, _log_type: 'partner' })))
            .catch(() => [])
        )
      }

      const results = await Promise.all(fetches)
      const merged = results.flat().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      setLogs(merged)
    } catch (e) {
      message.error(t('common.error_label'))
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin />
      </div>
    )
  }

  if (!logs || logs.length === 0) {
    return <Empty description={t('common.table.no_data')} />
  }

  const logTypeConfig = {
    platform: { color: 'blue', label: t('common.api_logs.type_platform') },
    webhook: { color: 'orange', label: t('common.api_logs.type_webhook') },
    partner: { color: 'purple', label: t('common.api_logs.type_partner') },
  }

  const renderLogHeader = (log) => {
    const typeTag = logTypeConfig[log._log_type] || { color: 'default', label: log._log_type }

    if (log._log_type === 'webhook') {
      const statusColorMap = { success: 'success', failed: 'error', pending: 'default', retrying: 'warning' }
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Tag color={typeTag.color}>{typeTag.label}</Tag>
          <Tag>{log.event_type}</Tag>
          <Tag color={statusColorMap[log.status] || 'default'}>{log.status}</Tag>
          {log.response_status && <Tag color={log.response_status < 300 ? 'success' : 'error'}>{log.response_status}</Tag>}
          <span style={{ fontSize: '12px', color: '#666', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.url}</span>
          {log.duration_ms != null && <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>{log.duration_ms.toFixed(0)} ms</span>}
          <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap' }}>{formatDateTime(log.created_at)}</span>
        </div>
      )
    }

    // platform & partner
    // 業務層狀態優先：Shopee 等平台業務失敗仍回 HTTP 200，需用 response_status (enum) 區分；
    // response_status 為 unknown（歷史資料）或缺值時 fallback 以 HTTP status 著色。
    const statusCode = log.response_http_status
    const statusTagColor = getStatusTagColor(log.response_status, statusCode)
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Tag color={typeTag.color}>{typeTag.label}</Tag>
        {log._log_type === 'partner' && log.action && (
          <Tag color="blue">{log.action}</Tag>
        )}
        <Tag>{log.request_method}</Tag>
        <Tag color={statusTagColor} title={`HTTP ${statusCode}`}>{statusCode}</Tag>
        <span style={{ fontSize: '12px', color: '#666', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.request_path}</span>
        {log._log_type === 'partner' && log.ip_address && (
          <Tag color="default" style={{ fontSize: '11px' }}>{log.ip_address}</Tag>
        )}
        {log.duration_ms != null && <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>{log.duration_ms.toFixed(0)} ms</span>}
        <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap' }}>{formatDateTime(log.created_at)}</span>
      </div>
    )
  }

  const preStyle = { background: '#f5f5f5', padding: '8px', borderRadius: '4px', margin: 0, fontSize: '11px', overflow: 'auto', maxHeight: '200px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
  const labelStyle = { fontSize: '11px', color: '#999', marginBottom: '4px' }

  const renderLogDetail = (log) => {
    if (log._log_type === 'webhook') {
      const payloadStr = log.payload ? (typeof log.payload === 'string' ? log.payload : JSON.stringify(log.payload, null, 2)) : '-'
      return (
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>{t('common.api_logs.payload')}</div>
            <pre style={preStyle}>{payloadStr}</pre>
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>{t('common.api_logs.response_body')}</div>
            <pre style={preStyle}>{log.response_body || '-'}</pre>
          </div>
        </div>
      )
    }

    return (
      <div style={{ display: 'flex', gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>{t('common.api_logs.request')}</div>
          <pre style={preStyle}>{log.request || '-'}</pre>
        </div>
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>{t('common.api_logs.response')}</div>
          <pre style={preStyle}>{log.response || '-'}</pre>
        </div>
      </div>
    )
  }

  return (
    <List
      dataSource={logs}
      pagination={{ pageSize: 10 }}
      size="small"
      renderItem={(log) => (
        <List.Item style={{ display: 'block', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ marginBottom: '8px' }}>
            {renderLogHeader(log)}
          </div>
          <Collapse size="small" items={[
            {
              key: 'detail',
              label: <span style={{ fontSize: '12px' }}>{t('common.api_logs.request')} / {t('common.api_logs.response')}</span>,
              children: renderLogDetail(log),
            }
          ]} />
        </List.Item>
      )}
    />
  )
}
